import React, { useEffect, useRef, useState } from "react";
import { API_URL } from "../../config";

const WorkerEntryEdit = ({ entry, onClose, onSaved }) => {
  const dialogRef = useRef(null);
  const busyRef = useRef(false);
  const [code, setCode] = useState(entry.code || "");
  const [activity, setActivity] = useState(entry.activity || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  const close = () => {
    if (!busyRef.current) onClose();
  };

  const save = async event => {
    event.preventDefault();
    if (busyRef.current) return;

    setError("");

    if (!code.trim() || !activity.trim()) {
      setError("Bitte Arbeitsort und Tätigkeit eingeben.");
      return;
    }

    // MariaDB-DATETIME ohne Zeitzonenangabe wird als UTC gespeichert.
    const originalTime = String(entry.time || "");
    const timeValue = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(originalTime)
      ? originalTime.replace(" ", "T") + "Z"
      : originalTime;

    const date = new Date(timeValue);

    if (Number.isNaN(date.getTime())) {
      setError("Die Erfassungszeit ist ungültig. Bitte lade die Seite neu.");
      return;
    }

    busyRef.current = true;
    setSaving(true);

    try {
      const response = await fetch(
        `${API_URL}/api/entries/${entry.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            code: code.trim(),
            activity: activity.trim(),
            time: date.toISOString(),
          }),
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || "Eintrag konnte nicht gespeichert werden.");
      }

      if (data?.success !== true) {
        throw new Error("Ungültige Serverantwort. Bitte prüfe den Verlauf.");
      }

      onSaved(data.changed);
    } catch (err) {
      setError(
        err instanceof TypeError
          ? "Keine Serverantwort erhalten. Prüfe vor einem erneuten Versuch den Verlauf."
          : err.message
      );
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="activity-modal worker-entry-dialog"
      aria-labelledby="worker-edit-title"
      onCancel={event => {
        event.preventDefault();
        close();
      }}
    >
      <div className="qr-modal-header">
        <h2 id="worker-edit-title">Eintrag bearbeiten</h2>
        <button
          type="button"
          className="qr-modal-close"
          onClick={close}
          disabled={saving}
          aria-label="Schließen"
        >
          ×
        </button>
      </div>

      <form className="qr-modal-content" onSubmit={save}>
        <label className="qr-input-label" htmlFor="worker-edit-code">
          Arbeitsort
        </label>
        <input
          id="worker-edit-code"
          className="worker-edit-input"
          value={code}
          onChange={event => setCode(event.target.value)}
          maxLength={500}
          disabled={saving}
          required
          autoFocus
        />

        <label className="qr-input-label" htmlFor="worker-edit-activity">
          Tätigkeit
        </label>
        <textarea
          id="worker-edit-activity"
          className="qr-activity-input"
          value={activity}
          onChange={event => setActivity(event.target.value)}
          maxLength={1000}
          rows={5}
          disabled={saving}
          required
        />

        <p>Die Erfassungszeit bleibt erhalten. Änderungen werden protokolliert.</p>
        {error && <p role="alert">{error}</p>}

        <button
          type="submit"
          className="qr-modal-primary"
          disabled={saving}
        >
          {saving ? "Wird gespeichert …" : "Änderungen speichern"}
        </button>

        <button
          type="button"
          className="qr-modal-secondary"
          onClick={close}
          disabled={saving}
        >
          Abbrechen
        </button>
      </form>
    </dialog>
  );
};

export default WorkerEntryEdit;